/**
 * F9·m3 — "Investigate with Copilot" seed context.
 *
 * A reusable, transport-agnostic description of a row the operator wants the
 * copilot to look into (an audit event, an alert, …). {@link buildInvestigatePrompt}
 * turns it into a starter prompt that pre-seeds the copilot composer. The prompt
 * is READ-ONLY guidance for the agent — it names the entity and its correlated
 * event ids so the agent can pull the right records via the read gateway tools.
 */
export interface InvestigateContext {
  /** What kind of row this is, e.g. 'audit event' or 'alert' — drives the phrasing. */
  readonly kind: string;
  /** The row's primary id, if any. */
  readonly id?: string;
  /** A short human summary of the row (message / action). */
  readonly summary?: string;
  /** Correlated event ids to hand the agent for a joined investigation. */
  readonly correlatedEventIds?: readonly string[];
  /** Extra labelled facts worth seeding (severity, user, symbol, …). */
  readonly facts?: Readonly<Record<string, string | number | undefined>>;
}

/**
 * Build the starter prompt seeded into the copilot composer. Deterministic and
 * side-effect-free so it is unit-testable; the caller (AgentChatService) decides
 * whether to auto-send (it does NOT — the operator reviews and sends).
 */
export function buildInvestigatePrompt(context: InvestigateContext): string {
  const kind = context.kind.trim() || 'event';
  const parts: string[] = [];

  const head = context.id
    ? `Investigate this ${kind} (id ${context.id}).`
    : `Investigate this ${kind}.`;
  parts.push(head);

  const summary = context.summary?.trim();
  if (summary) {
    parts.push(`Summary: ${summary}`);
  }

  const facts = context.facts;
  if (facts) {
    const rendered = Object.entries(facts)
      .filter(([, v]) => v !== undefined && v !== null && String(v).trim().length > 0)
      .map(([k, v]) => `${k}: ${String(v).trim()}`);
    if (rendered.length > 0) {
      parts.push(rendered.join(', '));
    }
  }

  const ids = (context.correlatedEventIds ?? [])
    .map((s) => String(s).trim())
    .filter((s) => s.length > 0);
  if (ids.length > 0) {
    parts.push(`Correlated event ids: ${ids.join(', ')}.`);
  }

  parts.push('What happened, and is anything anomalous?');
  return parts.join('\n');
}
